'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { formatPriceToNumber, useMediaQuery } from '@segway/core'
import isUndefined from 'lodash/isUndefined'

import { Link, useRouter } from '@/i18n/navigation'
import reportGTMEvent from '@/utils/gtm'

import { HomeButton } from '../Button'
import Sliders from '../slider/Sliders'
import { BestSellerType } from '../types'
import { ModuleTitle } from '../'

import BestSellerHelpMeChoose from './BestSellerHelpMeChoose'

import styles from './page.module.css'

// import Image from 'next/image'
// import { useMediaQuery } from '@segway/core'
// import { Carousel, CarouselItem } from '@/components'
// import { Arrow } from '@/components/icons'
// import { Link } from '@/i18n/navigation'
// import { Button } from '@/ui/button/Button'
// import { HomeButton } from '../Button'

interface HoverState {
  [key: string]: boolean
}

interface BestSellerProps {
  blockData: BestSellerType
  identify: string
}

const BestSeller = ({ blockData, identify }: BestSellerProps) => {
  const router = useRouter()
  const responsive = useMediaQuery()

  const isMobile = responsive && !responsive.xl

  const [hoverState, setHoverState] = useState<HoverState>({})

  const helpMeChooseBlockData = useMemo(
    () => blockData?.help_me_choose,
    [blockData?.help_me_choose],
  )

  const isHelpMeChooseBlockEnabled = useMemo(
    () => blockData?.help_me_choose?.is_enable === '1',
    [blockData?.help_me_choose?.is_enable],
  )

  const startHovering = useCallback((id: string | number) => {
    setHoverState((prev) => ({
      ...prev,
      [id]: true,
    }))
  }, [])

  const stopHovering = useCallback((id: string | number) => {
    setHoverState((prev) => ({
      ...prev,
      [id]: false,
    }))
  }, [])

  const isHovering = useCallback(
    (id: string | number) => {
      return hoverState[id] === true
    },
    [hoverState],
  )

  /**
   * GTM: select_item
   */
  const handleReportProductGTMEvent = useCallback(
    (data: BestSellerProps['blockData']['items'][number], index: number) => {
      return () => {
        const item_category = data.product.gtm_item_categories
          ? JSON.parse(data.product.gtm_item_categories)
          : {
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
            }

        reportGTMEvent({
          event: 'select_item',
          ecommerce: {
            item_list_name: identify,
            items: [
              {
                item_id: data.product.sku,
                item_name: data.product.name,
                msrp: formatPriceToNumber(data.product.price).numberValue,
                price: formatPriceToNumber(data.product.final_price).numberValue,
                item_brand: 'Segway',
                index,
                ...item_category,
              },
            ],
          },
        })
      }
    },
    [identify],
  )

  const sliderItems = [
    isHelpMeChooseBlockEnabled && helpMeChooseBlockData ? (
      <BestSellerHelpMeChoose helpMeChooseItem={helpMeChooseBlockData} />
    ) : null,
    ...blockData?.items.map((item, index) => (
      <div
        className={styles.item}
        key={index}
        onMouseEnter={() => startHovering(item.name)}
        onMouseLeave={() => stopHovering(item.name)}>
        <Link
          className={`relative flex ${
            index !== blockData?.items?.length - 1 ? (isMobile ? 'mr-3' : 'mr-4') : ''
          } h-[408px] w-[306px] flex-col justify-end overflow-hidden rounded-[12px] font-miSansDemiBold450 xl:h-[430px] xl:w-[348px] xl:rounded-[20px]`}
          href={item?.link}
          onClick={handleReportProductGTMEvent(item, index + 1)}>
          <div className="relative h-full w-full">
            <Image
              src={item?.pc_image}
              alt={item?.image_alt}
              width={348}
              height={430}
              className="absolute h-[408px] w-[306px] xl:h-[430px] xl:w-[348px]"
              style={{ objectFit: 'cover' }}
            />
          </div>
          {/* PC */}
          <div className="relative hidden flex-col gap-[12px] pb-[24px] pl-[27px] pr-[24px] xl:flex">
            <div className="flex flex-col gap-2 text-white">
              <p className="font-miSansDemiBold450 text-[20px] leading-[1.2]">{item?.name}</p>
              <p className="font-miSansRegular330 text-[14px] leading-[1.2]">{item?.description}</p>
            </div>
            <div className="flex h-[22px] items-center justify-between text-white">
              {(+item?.product?.final_price > 0 || +item?.product?.price > 0) && (
                <div className="flex gap-[4px]">
                  {+item?.product?.final_price > 0 && (
                    <p
                      className={`ml-0 flex items-center justify-center font-miSansDemiBold450 text-[16px]`}>
                      <span className="mr-[2px] text-[14px]">$</span>
                      {item?.product?.final_price}
                    </p>
                  )}
                  {+item?.product?.price > 0 &&
                    +item?.product?.final_price < +item?.product?.price && (
                      <p
                        className={`flex items-center justify-center font-miSansDemiBold450 text-[16px] ${item?.product?.final_price ? 'line-through' : ''} ${item?.product?.final_price ? 'text-[#86868B]' : 'text-white'}`}>
                        <span className="mr-[2px] text-[14px]">$</span>
                        {item?.product?.price}
                      </p>
                    )}
                </div>
              )}
            </div>
            <HomeButton
              hasArrowIcon={false}
              visible={isHovering(item?.name)}
              className="absolute bottom-[24px] right-[24px] h-[32px] gap-1 !px-[20px] py-[8px]"
              onClick={() => router.push(item?.link)}>
              <span className="font-miSansMedium380 text-[13px] leading-[16px]">Compra ahora</span>
            </HomeButton>
          </div>

          {/* Mobile */}
          <div className="relative flex flex-col gap-[12px] pb-[20px] pl-[14px] pr-[12px] xl:hidden">
            <div className="flex flex-col gap-[4px] text-white">
              <p className="font-miSansDemiBold450 text-[18px] leading-[22px]">{item?.name}</p>
              <p className="font-miSansRegular330 text-[14px] leading-[17px]">
                {item?.description}
              </p>
            </div>
            <div className="flex h-9 justify-between text-white">
              {(+item?.product?.final_price > 0 || +item?.product?.price > 0) && (
                <div className="flex gap-[4px]">
                  {+item?.product?.final_price > 0 && (
                    <p
                      className={`flex items-center justify-center font-miSansDemiBold450 text-[16px]`}>
                      <span className="mr-[1px] text-[12px]">$</span>
                      {item?.product?.final_price}
                    </p>
                  )}
                  {+item?.product?.price > 0 &&
                    +item?.product?.final_price < +item?.product?.price && (
                      <p
                        className={`flex items-center justify-center font-miSansDemiBold450 text-[16px] ${item?.product?.final_price ? 'line-through' : ''} ${item?.product?.final_price ? 'text-[#86868B]' : 'text-white'}`}>
                        <span className="mr-[1px] text-[12px]">$</span>
                        {item?.product?.price}
                      </p>
                    )}
                </div>
              )}
            </div>
          </div>
        </Link>
      </div>
    )),
  ]

  const filteredItems = sliderItems.filter((item) => item !== null && item !== undefined)

  /**
   * GTM: view_item_list
   */
  useEffect(() => {
    if (blockData?.items.length) {
      const filteredData = blockData?.items.filter((item) => !isUndefined(item?.product))

      const items = filteredData.map((item, index) => {
        const item_category = item.product.gtm_item_categories
          ? JSON.parse(item.product.gtm_item_categories)
          : {
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
            }

        return {
          item_id: item.product.sku,
          item_name: item.product.name,
          msrp: formatPriceToNumber(item.product.price).numberValue,
          price: formatPriceToNumber(item.product.final_price).numberValue,
          index: index + 1,
          item_brand: 'Segway',
          ...item_category,
        }
      })
      reportGTMEvent({
        event: 'view_item_list',
        ecommerce: {
          item_list_name: identify,
          items,
        },
      })
    }
  }, [identify, blockData])

  return blockData?.items && blockData?.items.length > 0 ? (
    <div className="flex w-full items-center justify-center">
      <div className="flex w-full flex-col justify-center">
        <div className="px-7 max-xl:px-4">
          <div className="mx-auto w-full 2xl:max-w-[1440px]">
            <ModuleTitle title={blockData?.name} viewAll="" />
          </div>
        </div>
        <div className="w-full">
          <Sliders gap={responsive && responsive.xl ? 16 : 12} items={filteredItems} />
          {/* {responsive && responsive.xl ? (
            <Carousel
              autoPlay={false}
              showIndicators={false}
              className="w-full"
              isHoverShowArrows={true}
              arrowHoverOption={{ btnClassName: 'bg-[#00000080]', iconColor: '#DA291C' }}
              arrowLeftContainerClassName="left-[6.25%]"
              arrowRightContainerClassName="right-[6.25%]"
              arrowIconDisableClassName="hidden">
              {blockData?.items.map((item, index) => (
                <CarouselItem
                  key={index}
                  className={`mr-4 w-fit basis-auto ${index === 0 ? 'ml-7 2xl:ml-[calc((100%-1440px)/2)]' : ''}`}>
                  <Link
                    className="relative flex h-[430px] w-[348px] flex-col justify-end overflow-hidden rounded-[20px]"
                    href={item?.link}>
                    <div>
                      <Image src={item?.pc_image} alt={item?.image_alt} fill objectFit="cover" />
                    </div>
                    <div className="relative flex flex-col gap-[12px] px-[24px] pb-[24px]">
                      <div className="flex flex-col gap-[4px] text-white">
                        <p className="h-7 text-[20px]">{item?.name}</p>
                        <p className="h-6 text-[14px] leading-[20px]">{item?.description}</p>
                      </div>
                      <div className="flex h-9 gap-[12px] text-white">
                        {item?.final_price ? (
                          <HomeButton className="h-[32px] py-[8px]">
                            <span className="font-miSansMedium380 text-[13px] leading-[16px]">
                              ${item?.final_price}
                            </span>{' '}
                          </HomeButton>
                        ) : null}
                        <p
                          className={`flex items-center justify-center font-miSansMedium380 text-[12px] ${item?.final_price ? 'line-through' : ''}`}>
                          ${item?.origin_price}
                        </p>
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </Carousel>
          ) : (
            <Carousel autoPlay={false} showIndicators={false} showArrows={false} className="w-full">
              {blockData?.items.map((item, index) => (
                <CarouselItem
                  key={index}
                  className={`mr-3 ${index === 0 ? 'ml-[16px]' : ''} ${index === blockData?.items?.length - 1 ? 'mr-[16px]' : ''} w-fit basis-auto`}>
                  <Link
                    className="relative flex h-[408px] w-[306px] flex-col justify-end overflow-hidden rounded-[12px]"
                    href={item?.link}>
                    <div>
                      <Image src={item?.mob_image} alt={item?.image_alt} fill objectFit="cover" />
                    </div>
                    <div className="relative flex flex-col gap-[12px] px-[12px] pb-[20px]">
                      <div className="flex flex-col gap-[4px] text-white">
                        <p className="h-[22px] text-[18px] leading-[22px]">{item?.name}</p>
                        <p className="h-[17px] text-[14px] leading-[17px]">{item?.description}</p>
                      </div>
                      <div className="flex h-9 gap-[12px] text-white">
                        {item?.final_price ? (
                          <Button className="h-[32px] py-[8px]">
                            <span className="font-miSansMedium380 text-[13px] leading-[16px]">
                              ${item?.final_price}
                            </span>{' '}
                            <Arrow width={16} height={16} color="white" />
                          </Button>
                        ) : null}
                        <p
                          className={`flex items-center justify-center font-miSansMedium380 text-[12px] ${item?.final_price ? 'line-through' : ''}`}>
                          ${item?.origin_price}
                        </p>
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </Carousel>
          )} */}
        </div>
      </div>
    </div>
  ) : null
}

export default BestSeller
